import React, { useState } from "react";
import {
  Settings,
  X,
  Table as TableIcon,
  Columns,
  LayoutGrid,
} from "lucide-react";
import type {
  ResponsiveTableWidths,
  ResponsiveColumnStyles,
  ResponsiveRowStyles,
  ResponsiveCellStyles,
} from "../types";
import { TableSettingsTab } from "./tabs/TableSettingsTab";
import { ColumnSettingsTab } from "./tabs/ColumnSettingsTab";
import { CellSettingsTab } from "./tabs/CellSettingsTab";

// Types for the unified settings
export interface TableSettings {
  tableWidths: ResponsiveTableWidths;
  tableBorderColor: string;
  tableBorderWidth: number;
  tableBorderRadius: number;
  tableRowGap: number;
}

export interface ColumnSettings {
  columnIndex: number;
  styles: ResponsiveColumnStyles;
}

export interface RowSettings {
  rowIndex: number;
  styles: ResponsiveRowStyles;
}

export interface CellSettings {
  styles: ResponsiveCellStyles;
}

interface UnifiedTableSettingsModalProps {
  isOpen: boolean;
  // Table settings
  tableSettings: TableSettings;
  onTableSettingsChange: (settings: Partial<TableSettings>) => void;
  // Column settings
  columnSettings: ColumnSettings;
  onColumnSettingsChange: (settings: Partial<ColumnSettings>) => void;
  onColumnSelect: (columnIndex: number) => void;
  totalColumns: number;
  // Row settings
  rowSettings: RowSettings;
  onRowSettingsChange: (settings: Partial<RowSettings>) => void;
  onRowSelect: (rowIndex: number) => void;
  totalRows: number;
  // Cell settings
  cellSettings: CellSettings;
  onCellSettingsChange: (settings: Partial<CellSettings>) => void;
  // Actions
  onApplyTableSettings: () => void;
  onApplyColumnSettings: () => void;
  onApplyRowSettings: () => void;
  onApplyCellSettings: () => void;
  onResetColumnWidths: () => void;
  onClose: () => void;
  onCloseWithoutReopen: () => void;
}

type TabType = "table" | "column" | "cell";

export const UnifiedTableSettingsModal: React.FC<UnifiedTableSettingsModalProps> = ({
  isOpen,
  tableSettings,
  onTableSettingsChange,
  columnSettings,
  onColumnSettingsChange,
  onColumnSelect,
  totalColumns,
  rowSettings,
  onRowSettingsChange,
  onRowSelect,
  totalRows,
  cellSettings,
  onCellSettingsChange,
  onApplyTableSettings,
  onApplyColumnSettings,
  onApplyRowSettings,
  onApplyCellSettings,
  onResetColumnWidths,
  onClose,
  onCloseWithoutReopen,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("table");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Semi-transparent backdrop on the right side only */}
      <div className="absolute inset-y-0 right-0 w-[520px] bg-black bg-opacity-10 pointer-events-auto" />
      {/* Modal positioned on the right side */}
      <div className="absolute right-4 top-4 bottom-4 w-[500px] bg-white rounded-lg shadow-xl flex flex-col pointer-events-auto overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Table Settings
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex px-4 overflow-x-auto">
            {([
              { id: "table", label: "Table", icon: TableIcon },
              { id: "column", label: "Column", icon: Columns },
              { id: "cell", label: "Cell", icon: LayoutGrid },
            ] as const).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  activeTab === id
                    ? "border-primary-main text-primary-main"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === "table" && (
            <TableSettingsTab
              tableSettings={tableSettings}
              onTableSettingsChange={onTableSettingsChange}
              onApplyTableSettings={onApplyTableSettings}
              onResetColumnWidths={onResetColumnWidths}
              onClose={onClose}
              onCloseWithoutReopen={onCloseWithoutReopen}
            />
          )}
          {activeTab === "column" && (
            <ColumnSettingsTab
              columnSettings={columnSettings}
              onColumnSettingsChange={onColumnSettingsChange}
              onColumnSelect={onColumnSelect}
              totalColumns={totalColumns}
              onApplyColumnSettings={onApplyColumnSettings}
              onClose={onClose}
              onCloseWithoutReopen={onCloseWithoutReopen}
            />
          )}
          {activeTab === "cell" && (
            <CellSettingsTab
              cellSettings={cellSettings}
              onCellSettingsChange={onCellSettingsChange}
              onApplyCellSettings={onApplyCellSettings}
              onClose={onClose}
              onCloseWithoutReopen={onCloseWithoutReopen}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
